// BES-9331: Guest-portal DOM mounts for registry form components
// (@besty/shared/formComponents), shared by SignAgreement (rental agreements)
// and FormRenderer (check-in forms). Both hydrate saved template HTML by
// attribute sweep; these functions render the interactive UI into each
// component's wrapper element. The isomorphic logic (validation, completeness,
// PDF markup) stays in shared; only DOM construction lives here.

import {
  EMPTY_ADDRESS,
  ExtractedFormComponent,
  FORM_COMPONENT_ID_ATTR,
  FORM_COMPONENT_TYPE_ATTR,
  FormUploadRecord,
  REQUIRED_ADDRESS_SUBFIELDS,
  COUNTRY_NAMES,
  escapeHtml,
  getFormComponentDescriptor,
  isAddressEmpty,
  parseAddressValue,
  serializeAddressValue,
  AddressSubfield,
  AddressValue,
} from '@besty/shared/formComponents';

type Translate = (key: string) => string;

/** Upload UI state for one attachment component (mirrors the customImages shape). */
export interface FormUploadUiState {
  record: FormUploadRecord | null;
  status: 'pending' | 'completed' | 'failed';
  fileName: string | null;
  error: string | null;
}

// Validation reasons come from the shared descriptors as fixed English
// strings; map them through literal t() calls so i18next static extraction
// finds the keys.
export const localizeFormComponentReason = (t: Translate, reason: string): string => {
  const known: Record<string, string> = {
    'Please enter a valid phone number': t('Please enter a valid phone number'),
    'Please enter a valid email address': t('Please enter a valid email address'),
    'Please complete all required address fields': t('Please complete all required address fields'),
    'This field is required': t('This field is required'),
  };
  return known[reason] ?? reason;
};

const INPUT_STYLE =
  'width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;font-family:inherit;color:inherit;background:#fff;';
const LABEL_STYLE = 'display:block;font-weight:500;margin-bottom:6px;color:inherit;';
const SUBLABEL_STYLE = 'display:block;font-size:12px;color:#64748b;margin-bottom:4px;';
const ERROR_STYLE = 'display:none;color:#dc2626;font-size:12px;margin-top:4px;';
const REQUIRED_MARK = ' <span style="color:#dc2626;">*</span>';

/**
 * Clear the editor placeholder's card styling (dashed border, tinted
 * background, padding) so the field flows with the agreement text, matching
 * how the multi-select mount neutralizes its wrapper.
 */
const neutralizeWrapperStyles = (el: HTMLElement): void => {
  el.style.margin = '16px 0';
  el.style.padding = '0';
  el.style.background = 'transparent';
  el.style.border = 'none';
  el.style.borderRadius = '0';
  el.style.textAlign = 'left';
  el.style.minHeight = '0';
  el.style.color = 'inherit';
};

const fieldLabelHtml = (field: ExtractedFormComponent): string =>
  `<label style="${LABEL_STYLE}">${escapeHtml(field.label)}${field.required ? REQUIRED_MARK : ''}</label>`;

/**
 * Build an ExtractedFormComponent from a live wrapper element, mirroring the
 * semantics of shared extractFormComponents (which parses HTML strings).
 * getAttribute already entity-decodes, so no explicit decode step is needed.
 * Returns null for unknown types (forward compatibility, same as extraction).
 */
export function readFormComponentFromElement(el: HTMLElement): ExtractedFormComponent | null {
  const id = el.getAttribute(FORM_COMPONENT_ID_ATTR);
  const type = el.getAttribute(FORM_COMPONENT_TYPE_ATTR);
  if (!id || !type) return null;
  const descriptor = getFormComponentDescriptor(type);
  if (!descriptor) return null;

  const label = el.getAttribute('data-label') || descriptor.defaultLabel;
  const rawRequired = el.getAttribute('data-required');
  const required = rawRequired === null ? descriptor.requiredDefault : rawRequired === 'true';

  const attrs: Record<string, string> = {};
  for (const spec of descriptor.extraAttrs) {
    attrs[spec.attr] = el.getAttribute(spec.attr) ?? spec.defaultValue;
  }

  return { id, type, label, originalLabel: label, required, attrs };
}

export interface ValueInputMountOptions {
  el: HTMLElement;
  field: ExtractedFormComponent;
  initialValue: string;
  disabled: boolean;
  t: Translate;
  /**
   * Persists the value (state update + progress PATCH). May return the
   * persistence promise; flush fns pass it through so callers that navigate
   * away (flushInputSaves) can await the write instead of racing it.
   */
  onSave: (value: string) => void | Promise<void>;
  /** Registers a flush fn that saves any pending debounced value immediately. */
  registerFlush: (flush: () => void | Promise<void>) => void;
}

/** Phone and email inputs: styled single input, blur + 1s-debounce saves, inline validation. */
export function mountFormComponentTextInput({
  el,
  field,
  initialValue,
  disabled,
  t,
  onSave,
  registerFlush,
}: ValueInputMountOptions): void {
  const isPhone = field.type === 'phone-input';
  neutralizeWrapperStyles(el);
  el.innerHTML = `
    ${fieldLabelHtml(field)}
    <input
      type="${isPhone ? 'tel' : 'email'}"
      inputmode="${isPhone ? 'tel' : 'email'}"
      autocomplete="${isPhone ? 'tel' : 'email'}"
      placeholder="${escapeHtml(isPhone ? t('Enter phone number') : t('Enter email address'))}"
      style="${INPUT_STYLE}"
      ${disabled ? 'disabled' : ''}
    />
    <div data-fc-error style="${ERROR_STYLE}"></div>
  `;
  const input = el.querySelector('input') as HTMLInputElement;
  const errorEl = el.querySelector('[data-fc-error]') as HTMLElement;
  input.value = initialValue;
  if (disabled) {
    input.style.cursor = 'not-allowed';
    return;
  }

  const descriptor = getFormComponentDescriptor(field.type);
  const showValidation = () => {
    const reason = descriptor?.validate(field, input.value) ?? null;
    if (reason) {
      errorEl.textContent = localizeFormComponentReason(t, reason);
      errorEl.style.display = 'block';
    } else {
      errorEl.style.display = 'none';
    }
  };

  let saveTimer: number | undefined;
  let lastSaved = initialValue;
  const save = (): void | Promise<void> => {
    const value = input.value.trim();
    if (value === lastSaved) return undefined;
    lastSaved = value;
    return onSave(value);
  };
  input.addEventListener('blur', () => {
    window.clearTimeout(saveTimer);
    void save();
    showValidation();
  });
  input.addEventListener('input', () => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(save, 1000);
  });
  registerFlush(() => {
    window.clearTimeout(saveTimer);
    return save();
  });
}

/** Checkout-style address block: country dropdown + free-text subfields, saved as one JSON value. */
export function mountFormComponentAddress({
  el,
  field,
  initialValue,
  disabled,
  t,
  onSave,
  registerFlush,
}: ValueInputMountOptions): void {
  neutralizeWrapperStyles(el);
  const value: AddressValue = parseAddressValue(initialValue) || { ...EMPTY_ADDRESS };
  const mark = (key: AddressSubfield): string =>
    field.required && (REQUIRED_ADDRESS_SUBFIELDS as string[]).includes(key) ? REQUIRED_MARK : '';

  const countryOptions = [
    `<option value="">${escapeHtml(t('Select country'))}</option>`,
    ...COUNTRY_NAMES.map((name) => `<option value="${escapeHtml(name)}">${escapeHtml(name)}</option>`),
  ].join('');

  el.innerHTML = `
    ${fieldLabelHtml(field)}
    <div style="display:flex;flex-direction:column;gap:10px;">
      <div>
        <span style="${SUBLABEL_STYLE}">${escapeHtml(t('Country'))}${mark('country')}</span>
        <select data-fc-address="country" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''}>${countryOptions}</select>
      </div>
      <div>
        <span style="${SUBLABEL_STYLE}">${escapeHtml(t('Street address'))}${mark('line1')}</span>
        <input data-fc-address="line1" type="text" autocomplete="address-line1" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''} />
      </div>
      <div>
        <span style="${SUBLABEL_STYLE}">${escapeHtml(t('Apartment, suite, unit (optional)'))}</span>
        <input data-fc-address="line2" type="text" autocomplete="address-line2" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''} />
      </div>
      <div style="display:flex;gap:10px;flex-wrap:wrap;">
        <div style="flex:2;min-width:140px;">
          <span style="${SUBLABEL_STYLE}">${escapeHtml(t('City'))}${mark('city')}</span>
          <input data-fc-address="city" type="text" autocomplete="address-level2" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''} />
        </div>
        <div style="flex:1;min-width:110px;">
          <span style="${SUBLABEL_STYLE}">${escapeHtml(t('State / Province'))}${mark('state')}</span>
          <input data-fc-address="state" type="text" autocomplete="address-level1" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''} />
        </div>
        <div style="flex:1;min-width:110px;">
          <span style="${SUBLABEL_STYLE}">${escapeHtml(t('ZIP / Postal code'))}${mark('zip')}</span>
          <input data-fc-address="zip" type="text" autocomplete="postal-code" style="${INPUT_STYLE}" ${disabled ? 'disabled' : ''} />
        </div>
      </div>
    </div>
    <div data-fc-error style="${ERROR_STYLE}"></div>
  `;

  const subInputs: Record<AddressSubfield, HTMLInputElement | HTMLSelectElement> = {} as any;
  el.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-fc-address]').forEach((input) => {
    const key = input.getAttribute('data-fc-address') as AddressSubfield;
    subInputs[key] = input;
    input.value = value[key] || '';
  });
  if (disabled) return;

  const errorEl = el.querySelector('[data-fc-error]') as HTMLElement;
  const descriptor = getFormComponentDescriptor(field.type);

  const currentValue = (): string => {
    const address: AddressValue = { ...EMPTY_ADDRESS };
    (Object.keys(subInputs) as AddressSubfield[]).forEach((key) => {
      address[key] = subInputs[key].value.trim();
    });
    // An untouched/cleared address saves as empty string so absence and
    // "all subfields blank" are the same state everywhere.
    return isAddressEmpty(address) ? '' : serializeAddressValue(address);
  };
  const showValidation = () => {
    const reason = descriptor?.validate(field, currentValue()) ?? null;
    if (reason) {
      errorEl.textContent = localizeFormComponentReason(t, reason);
      errorEl.style.display = 'block';
    } else {
      errorEl.style.display = 'none';
    }
  };

  let saveTimer: number | undefined;
  let lastSaved = initialValue;
  const save = (): void | Promise<void> => {
    const serialized = currentValue();
    if (serialized === lastSaved) return undefined;
    lastSaved = serialized;
    return onSave(serialized);
  };
  (Object.keys(subInputs) as AddressSubfield[]).forEach((key) => {
    const input = subInputs[key];
    input.addEventListener('change', () => {
      window.clearTimeout(saveTimer);
      void save();
      showValidation();
    });
    input.addEventListener('input', () => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(save, 1000);
    });
  });
  registerFlush(() => {
    window.clearTimeout(saveTimer);
    return save();
  });
}

export interface AttachmentMountOptions {
  el: HTMLElement;
  field: ExtractedFormComponent;
  upload: FormUploadUiState | undefined;
  disabled: boolean;
  t: Translate;
  /** Opens the hidden file input for this component. */
  onPickFile: () => void;
}

/** Attachment upload card: rebuilt from upload state on every mount pass (mirrors custom images). */
export function mountFormComponentAttachment({ el, field, upload, disabled, t, onPickFile }: AttachmentMountOptions): void {
  neutralizeWrapperStyles(el);
  const record = upload?.record;

  let body = '';
  if (upload?.status === 'pending') {
    body = `<div style="color:#64748b;font-size:14px;">${escapeHtml(t('Uploading...'))}</div>`;
  } else if (upload?.status === 'completed' && record) {
    const isImage = !!record.mimeType && record.mimeType.startsWith('image/');
    const preview = isImage
      ? `<img src="${escapeHtml(record.url)}" style="max-width:min(400px,100%);max-height:300px;object-fit:contain;border-radius:6px;border:1px solid #d1d5db;display:block;margin:0 auto 10px;" />`
      : '';
    body = `
      ${preview}
      <div style="display:flex;align-items:center;justify-content:center;gap:8px;font-size:14px;color:#1e293b;">
        <span>&#128206;</span>
        <span style="word-break:break-all;">${escapeHtml(upload.fileName || record.fileName || t('Attachment'))}</span>
        <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:#059669;border-radius:50%;color:white;font-size:12px;">&#10003;</span>
      </div>
      ${
        disabled
          ? ''
          : `<button type="button" data-fc-pick style="margin-top:10px;background:#6b7280;color:#fff;border:none;border-radius:6px;padding:8px 12px;cursor:pointer;font-size:.875rem;">${escapeHtml(t('Replace file'))}</button>`
      }
    `;
  } else {
    const error = upload?.status === 'failed' && upload.error ? upload.error : null;
    body = `
      ${error ? `<div style="color:#dc2626;font-size:13px;margin-bottom:8px;">${escapeHtml(error)}</div>` : ''}
      <button type="button" data-fc-pick ${disabled ? 'disabled' : ''} style="background:${disabled ? '#9ca3af' : '#2563eb'};color:#fff;border:none;border-radius:6px;padding:10px 16px;cursor:${disabled ? 'not-allowed' : 'pointer'};font-size:.875rem;">${escapeHtml(t('Upload file'))}</button>
      <div style="font-size:12px;color:#94a3b8;margin-top:6px;">${escapeHtml(t('PDF or image, up to 25MB'))}</div>
    `;
  }

  el.innerHTML = `
    <div style="border:2px dashed #cbd5e1;border-radius:8px;padding:16px;text-align:center;background:#f8fafc;">
      <div style="font-weight:500;margin-bottom:10px;color:#1e293b;">${escapeHtml(field.label)}${field.required ? REQUIRED_MARK : ''}</div>
      ${body}
    </div>
  `;

  if (!disabled) {
    el.querySelector<HTMLButtonElement>('[data-fc-pick]')?.addEventListener('click', (e) => {
      e.preventDefault();
      onPickFile();
    });
  }
}
